import React, { useState } from 'react';
import {
  collection,
  doc,
  setDoc,
  writeBatch,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Swal from 'sweetalert2';

interface Stats {
  agencias: number;
  asesores: number;
  asociados: number;
  propiedades: number;
  errores: number;
}

interface MapeoAgencias {
  [key: string]: {
    docId: string;
    nombre: string;
  };
}

interface MapeoMiembros {
  [key: string]: {
    docId: string;
    tipo: 'asesor' | 'asociado';
    nombre: string;
  };
}

const MigracionOptimizada: React.FC = () => {
  const [ejecutando, setEjecutando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [stats, setStats] = useState<Stats>({
    agencias: 0,
    asesores: 0,
    asociados: 0,
    propiedades: 0,
    errores: 0
  });
  const [logs, setLogs] = useState<string[]>([]);

  // Mapeos para referencias
  const mapeoAgencias: MapeoAgencias = {};
  const mapeoMiembros: MapeoMiembros = {};
  const mapeoAuth: { [correo: string]: { nombre: string; correo: string } } = {};

  const addLog = (mensaje: string) => {
    console.log(mensaje);
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()} - ${mensaje}`]);
  };

  const normalizeString = (str: string): string => {
    if (!str) return '';
    return str.toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .trim();
  };

  const parseCSV = (csv: string): any[] => {
    const lines = csv.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] ? values[index].trim() : '';
      });
      data.push(row);
    }

    return data;
  };

  const cargarCSV = async (filename: string): Promise<string> => {
    const response = await fetch(`${process.env.PUBLIC_URL}/data-migration/${filename}`);
    if (!response.ok) {
      throw new Error(`No se pudo cargar ${filename}`);
    }
    return await response.text();
  };

  const cargarAuth = async () => {
    addLog('📋 Cargando usuarios de Authentication...');
    const response = await fetch(`${process.env.PUBLIC_URL}/resumen-usuarios-auth.json`);
    const authData = await response.json();

    authData.usuarios.forEach((usuario: any) => {
      const correoNormalizado = usuario.email.toLowerCase().trim();
      mapeoAuth[correoNormalizado] = {
        nombre: usuario.nombre,
        correo: usuario.email
      };
    });

    addLog(`✅ ${Object.keys(mapeoAuth).length} usuarios cargados`);
  };

  const migrarAgencias = async (csv: string) => {
    addLog('🏢 Migrando agencias...');
    setProgreso(20);

    const agenciasCSV = parseCSV(csv);
    let batch = writeBatch(db);
    let operacionesEnBatch = 0;
    let procesados = 0;

    for (const agenciaCSV of agenciasCSV) {
      try {
        const agenciaRef = doc(collection(db, 'inmobiliaria'));
        const docId = agenciaRef.id;

        const agenciaData = {
          nombre: agenciaCSV.nombre || '',
          correo: agenciaCSV.correo?.toLowerCase().trim() || '',
          telefono: agenciaCSV.telefono || '',
          ciudad: agenciaCSV.ciudad || 'Puerto Vallarta',
          estado: agenciaCSV.estado || 'Jalisco',
          codigo_postal: agenciaCSV.codigoPostal || '',
          sitio_web: agenciaCSV.sitioWeb || '',
          descripcion: '',
          logo_url: '',
          lat: agenciaCSV.lat ? parseFloat(agenciaCSV.lat) : null,
          lng: agenciaCSV.lng ? parseFloat(agenciaCSV.lng) : null,
          asesor_doc_ids: [],
          miembro_doc_ids: [],
          activo: true,
          fecha_creacion: serverTimestamp(),
          id_original_csv: agenciaCSV.id || ''
        };

        batch.set(agenciaRef, agenciaData);
        operacionesEnBatch++;

        const nombreNormalizado = normalizeString(agenciaCSV.nombre);
        mapeoAgencias[nombreNormalizado] = {
          docId: docId,
          nombre: agenciaCSV.nombre
        };

        if (agenciaCSV.id) {
          mapeoAgencias[agenciaCSV.id] = {
            docId: docId,
            nombre: agenciaCSV.nombre
          };
        }

        procesados++;
        setStats(prev => ({ ...prev, agencias: procesados }));

        if (operacionesEnBatch >= 500) {
          await batch.commit();
          addLog(`   ✅ Batch committed: ${procesados}/${agenciasCSV.length} agencias`);
          batch = writeBatch(db);
          operacionesEnBatch = 0;
        }
      } catch (error: any) {
        addLog(`   ❌ Error agencia ${agenciaCSV.nombre}: ${error.message}`);
        setStats(prev => ({ ...prev, errores: prev.errores + 1 }));
      }
    }

    if (operacionesEnBatch > 0) {
      await batch.commit();
    }

    addLog(`✅ ${procesados} agencias migradas`);
  };

  const migrarAsesores = async (asesores: any[]) => {
    addLog(`📋 Migrando ${asesores.length} asesores (Afiliados)...`);

    let batch = writeBatch(db);
    let operacionesEnBatch = 0;
    let procesados = 0;

    for (const asesorCSV of asesores) {
      try {
        const asesorRef = doc(collection(db, 'asesor'));
        const docId = asesorRef.id;

        const agenciasDelAsesor = [];
        if (asesorCSV.agencia) {
          const nombreAgenciaNormalizado = normalizeString(asesorCSV.agencia);
          const agenciaMapeo = mapeoAgencias[nombreAgenciaNormalizado];

          if (agenciaMapeo) {
            agenciasDelAsesor.push(agenciaMapeo.docId);
          }
        }

        const correoNormalizado = asesorCSV.correo?.toLowerCase().trim() || '';

        const asesorData = {
          tipo: 'asesor', // Campo requerido para resolverRol
          nombre: asesorCSV.nombre || '',
          correo: correoNormalizado,
          telefono: asesorCSV.telefono || '',
          inmobiliaria_doc_ids: agenciasDelAsesor,
          sitio_web: asesorCSV.sitioWeb || '',
          foto_url: '',
          biografia: '',
          especialidad: '',
          facebook: '',
          instagram: '',
          linkedin: '',
          propiedades_activas: 0,
          propiedades_vendidas: 0,
          valoracion: 0,
          activo: true,
          aprobado: true,
          destacado: false,
          fecha_creacion: serverTimestamp(),
          id_original_csv: asesorCSV.id || '',
          auth_uid: mapeoAuth[correoNormalizado] ? correoNormalizado : ''
        };

        batch.set(asesorRef, asesorData);
        operacionesEnBatch++;

        mapeoMiembros[asesorCSV.id || asesorCSV.nombre] = {
          docId: docId,
          tipo: 'asesor',
          nombre: asesorCSV.nombre
        };

        procesados++;
        setStats(prev => ({ ...prev, asesores: procesados }));

        if (operacionesEnBatch >= 500) {
          await batch.commit();
          batch = writeBatch(db);
          operacionesEnBatch = 0;
        }
      } catch (error: any) {
        addLog(`   ❌ Error asesor ${asesorCSV.nombre}: ${error.message}`);
        setStats(prev => ({ ...prev, errores: prev.errores + 1 }));
      }
    }

    if (operacionesEnBatch > 0) {
      await batch.commit();
    }

    addLog(`✅ ${procesados} asesores migrados`);
  };

  const migrarAsociados = async (asociados: any[]) => {
    addLog(`📋 Migrando ${asociados.length} asociados...`);

    let batch = writeBatch(db);
    let operacionesEnBatch = 0;
    let procesados = 0;

    for (const asociadoCSV of asociados) {
      try {
        const asociadoRef = doc(collection(db, 'asociado'));
        const docId = asociadoRef.id;

        let agenciaDocId = null;
        if (asociadoCSV.agencia) {
          const nombreAgenciaNormalizado = normalizeString(asociadoCSV.agencia);
          const agenciaMapeo = mapeoAgencias[nombreAgenciaNormalizado];

          if (agenciaMapeo) {
            agenciaDocId = agenciaMapeo.docId;
          }
        }

        const correoNormalizado = asociadoCSV.correo?.toLowerCase().trim() || '';

        const asociadoData = {
          tipo: 'asociado', // Campo requerido para resolverRol
          nombre: asociadoCSV.nombre || '',
          correo: correoNormalizado,
          telefono: asociadoCSV.telefono || '',
          inmobiliaria_doc_id: agenciaDocId,
          sitio_web: asociadoCSV.sitioWeb || '',
          foto_url: '',
          biografia: '',
          especialidad: '',
          facebook: '',
          instagram: '',
          linkedin: '',
          propiedades_activas: 0,
          propiedades_vendidas: 0,
          valoracion: 0,
          activo: true,
          aprobado: true,
          destacado: false,
          fecha_creacion: serverTimestamp(),
          id_original_csv: asociadoCSV.id || '',
          auth_uid: mapeoAuth[correoNormalizado] ? correoNormalizado : ''
        };

        batch.set(asociadoRef, asociadoData);
        operacionesEnBatch++;

        mapeoMiembros[asociadoCSV.id || asociadoCSV.nombre] = {
          docId: docId,
          tipo: 'asociado',
          nombre: asociadoCSV.nombre
        };

        procesados++;
        setStats(prev => ({ ...prev, asociados: procesados }));

        if (operacionesEnBatch >= 500) {
          await batch.commit();
          batch = writeBatch(db);
          operacionesEnBatch = 0;
        }
      } catch (error: any) {
        addLog(`   ❌ Error asociado ${asociadoCSV.nombre}: ${error.message}`);
        setStats(prev => ({ ...prev, errores: prev.errores + 1 }));
      }
    }

    if (operacionesEnBatch > 0) {
      await batch.commit();
    }

    addLog(`✅ ${procesados} asociados migrados`);
  };

  const actualizarRelacionesAgencias = async () => {
    addLog('🔗 Actualizando relaciones bidireccionales...');
    setProgreso(60);

    const asesoresSnapshot = await getDocs(collection(db, 'asesor'));
    const asesoresPorAgencia: { [key: string]: string[] } = {};

    asesoresSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.inmobiliaria_doc_ids && Array.isArray(data.inmobiliaria_doc_ids)) {
        data.inmobiliaria_doc_ids.forEach((agenciaId: string) => {
          if (!asesoresPorAgencia[agenciaId]) {
            asesoresPorAgencia[agenciaId] = [];
          }
          asesoresPorAgencia[agenciaId].push(docSnap.id);
        });
      }
    });

    const asociadosSnapshot = await getDocs(collection(db, 'asociado'));
    const asociadosPorAgencia: { [key: string]: string[] } = {};

    asociadosSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.inmobiliaria_doc_id) {
        if (!asociadosPorAgencia[data.inmobiliaria_doc_id]) {
          asociadosPorAgencia[data.inmobiliaria_doc_id] = [];
        }
        asociadosPorAgencia[data.inmobiliaria_doc_id].push(docSnap.id);
      }
    });

    const agenciasSnapshot = await getDocs(collection(db, 'inmobiliaria'));
    let batch = writeBatch(db);
    let operacionesEnBatch = 0;

    for (const agenciaDoc of agenciasSnapshot.docs) {
      const agenciaId = agenciaDoc.id;
      const asesorDocIds = asesoresPorAgencia[agenciaId] || [];
      const miembroDocIds = asociadosPorAgencia[agenciaId] || [];

      batch.update(agenciaDoc.ref, {
        asesor_doc_ids: asesorDocIds,
        miembro_doc_ids: miembroDocIds
      });

      operacionesEnBatch++;

      if (operacionesEnBatch >= 500) {
        await batch.commit();
        batch = writeBatch(db);
        operacionesEnBatch = 0;
      }
    }

    if (operacionesEnBatch > 0) {
      await batch.commit();
    }

    addLog(`✅ Relaciones actualizadas`);
  };

  const migrarPropiedades = async (csv: string) => {
    addLog('🏠 Migrando propiedades...');
    setProgreso(70);

    const propiedadesCSV = parseCSV(csv);
    let batch = writeBatch(db);
    let operacionesEnBatch = 0;
    let procesados = 0;

    for (const propCSV of propiedadesCSV) {
      try {
        let inmobiliariaDocId = null;
        if (propCSV.agenciaNombre) {
          const nombreAgenciaNormalizado = normalizeString(propCSV.agenciaNombre);
          const agenciaMapeo = mapeoAgencias[nombreAgenciaNormalizado];
          if (agenciaMapeo) {
            inmobiliariaDocId = agenciaMapeo.docId;
          }
        }

        const asesorDocIds: string[] = [];
        if (propCSV.agenteNombre) {
          const nombreAsesorNormalizado = normalizeString(propCSV.agenteNombre);
          const miembroMapeo = Object.values(mapeoMiembros).find(m =>
            normalizeString(m.nombre) === nombreAsesorNormalizado
          );
          if (miembroMapeo) {
            asesorDocIds.push(miembroMapeo.docId);
          }
        }

        const propiedadRef = doc(collection(db, 'propiedades'));

        // Procesar amenidades desde Features
        const amenidades: string[] = [];
        if (propCSV.features) {
          // Features viene en formato: "key|subkey|value;key2|subkey2|value2;"
          const featuresArray = propCSV.features.split(';').filter((f: string) => f.trim());
          featuresArray.forEach((feature: string) => {
            const parts = feature.split('|');
            if (parts.length >= 2) {
              // Tomar la parte descriptiva (generalmente la segunda o tercera parte)
              const amenidad = parts[1]?.trim() || parts[0]?.trim();
              if (amenidad && amenidad.length > 2) {
                amenidades.push(amenidad);
              }
            }
          });
        }

        const propiedadData = {
          titulo: propCSV.listNumber || 'Sin título',
          descripcion: propCSV.description || '',
          tipo_propiedad: propCSV.tipo || 'Casa',
          formato_propiedad: propCSV.formato || 'Venta',
          estado: propCSV.estado || 'Activo',
          direccion: {
            calle: '',
            numero: '',
            unidad: '',
            area: propCSV.area || propCSV.comunidad || 'Riviera Nayarit',
            zona: 'Riviera Nayarit',
            ciudad: 'Puerto Vallarta',
            estado: 'Jalisco',
            codigo_postal: ''
          },
          recamaras: parseInt(propCSV.recamaras) || 0,
          banos_completos: parseInt(propCSV.banos) || 0,
          medios_banos: 0,
          m2_total: parseFloat(propCSV.totalM2) || 0,
          m2_lote: 0,
          pisos: 1,
          anos_construccion: '',
          estacionamiento: 0,
          amueblado: 'No especificado',
          precio_actual_usd: parseFloat(propCSV.precioUSD) || 0,
          precio_actual_mxn: parseFloat(propCSV.precioMXN) || 0,
          fotos: [],
          video_url: '',
          tour_virtual_url: '',
          flex_url: '',
          amenidades: amenidades,
          inmobiliaria_doc_id: inmobiliariaDocId,
          asesor_doc_ids: asesorDocIds,
          activo: true,
          aprobado: true,
          destacada: false,
          fecha_creacion: serverTimestamp(),
          list_number: propCSV.listNumber || '',
          flex_id: ''
        };

        batch.set(propiedadRef, propiedadData);
        operacionesEnBatch++;
        procesados++;
        setStats(prev => ({ ...prev, propiedades: procesados }));

        if (operacionesEnBatch >= 500) {
          await batch.commit();
          addLog(`   ✅ Batch committed: ${procesados}/${propiedadesCSV.length} propiedades`);
          batch = writeBatch(db);
          operacionesEnBatch = 0;
        }
      } catch (error: any) {
        addLog(`   ❌ Error propiedad ${propCSV.listNumber}: ${error.message}`);
        setStats(prev => ({ ...prev, errores: prev.errores + 1 }));
      }
    }

    if (operacionesEnBatch > 0) {
      await batch.commit();
    }

    addLog(`✅ ${procesados} propiedades migradas`);
  };

  const ejecutarMigracion = async () => {
    const resultado = await Swal.fire({
      title: '⚠️ CONFIRMACIÓN DE MIGRACIÓN',
      html: `
        <div style="text-align: left; padding: 20px;">
          <p><strong>Esta acción creará:</strong></p>
          <ul>
            <li>✅ 127 agencias</li>
            <li>✅ ~180 asesores (Afiliados)</li>
            <li>✅ ~95 asociados</li>
            <li>✅ 3,432 propiedades</li>
          </ul>
          <p style="color: red; font-weight: bold;">⚠️ Asegúrate de que las colecciones estén vacías</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4CAF50',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, ejecutar migración',
      cancelButtonText: 'Cancelar'
    });

    if (!resultado.isConfirmed) return;

    setEjecutando(true);
    setLogs([]);
    setStats({ agencias: 0, asesores: 0, asociados: 0, propiedades: 0, errores: 0 });
    setProgreso(0);

    try {
      addLog('🚀 Iniciando migración completa...');

      // FASE 0: Cargar Auth
      setProgreso(5);
      await cargarAuth();

      // FASE 1: Migrar Agencias
      setProgreso(10);
      const agenciasCSV = await cargarCSV('agencias-limpias.csv');
      await migrarAgencias(agenciasCSV);

      // FASE 2: Migrar Miembros
      setProgreso(35);
      const miembrosCSV = await cargarCSV('miembros-limpios.csv');
      const miembros = parseCSV(miembrosCSV);
      const asesores = miembros.filter(m => m.tipo === 'Afiliado');
      const asociados = miembros.filter(m => m.tipo === 'Asociado');

      await migrarAsesores(asesores);
      setProgreso(45);
      await migrarAsociados(asociados);

      // FASE 3: Actualizar Relaciones
      setProgreso(55);
      await actualizarRelacionesAgencias();

      // FASE 4: Migrar Propiedades (con description y features)
      setProgreso(65);
      const propiedadesCSV = await cargarCSV('propiedades-completas.csv');
      await migrarPropiedades(propiedadesCSV);

      setProgreso(100);
      addLog('✅ ¡MIGRACIÓN COMPLETADA EXITOSAMENTE!');

      await Swal.fire({
        title: '✅ ¡Éxito!',
        html: `
          <div style="text-align: left;">
            <p><strong>Migración completada:</strong></p>
            <ul>
              <li>Agencias: ${stats.agencias}</li>
              <li>Asesores: ${stats.asesores}</li>
              <li>Asociados: ${stats.asociados}</li>
              <li>Propiedades: ${stats.propiedades}</li>
              <li>Errores: ${stats.errores}</li>
            </ul>
          </div>
        `,
        icon: 'success'
      });

    } catch (error: any) {
      addLog(`❌ ERROR CRÍTICO: ${error.message}`);
      await Swal.fire({
        title: '❌ Error',
        text: error.message,
        icon: 'error'
      });
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>
        🔄 Migración Optimizada - Resido Properties
      </h1>

      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={ejecutarMigracion}
          disabled={ejecutando}
          style={{
            padding: '15px 30px',
            fontSize: '18px',
            backgroundColor: ejecutando ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: ejecutando ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
        >
          {ejecutando ? '⏳ Migrando...' : '▶️ Iniciar Migración'}
        </button>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Progreso:</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{progreso}%</div>
        </div>
      </div>

      {/* Estadísticas */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '10px',
        marginBottom: '30px'
      }}>
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '15px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Agencias</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.agencias}</div>
        </div>
        <div style={{
          backgroundColor: '#f3e5f5',
          padding: '15px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Asesores</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.asesores}</div>
        </div>
        <div style={{
          backgroundColor: '#fff3e0',
          padding: '15px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Asociados</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.asociados}</div>
        </div>
        <div style={{
          backgroundColor: '#e8f5e9',
          padding: '15px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Propiedades</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{stats.propiedades}</div>
        </div>
        <div style={{
          backgroundColor: '#ffebee',
          padding: '15px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Errores</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>{stats.errores}</div>
        </div>
      </div>

      {/* Log */}
      <div style={{
        backgroundColor: '#263238',
        padding: '20px',
        borderRadius: '8px',
        color: '#fff'
      }}>
        <h3 style={{ marginTop: 0, color: '#4CAF50' }}>📋 Log de Migración:</h3>
        <div style={{
          maxHeight: '500px',
          overflow: 'auto',
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '13px',
          lineHeight: '1.6'
        }}>
          {logs.length === 0 && (
            <div style={{ color: '#888', fontStyle: 'italic' }}>
              Esperando inicio de migración...
            </div>
          )}
          {logs.map((entry, index) => (
            <div key={index} style={{ marginBottom: '5px' }}>
              {entry}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MigracionOptimizada;
